import { gql } from "lib/apolloClient";
import { plannerFieldFragment } from "UnitPlans/modules/UnitPlanFragments";
import {
  expertProfileFragment,
  organizationFragment,
  plannerTemplateFragment,
  courseFragment,
  curriculumProgramFragment,
  academicYearFragment,
  userNameFragment,
} from "modules/CommonFragments";

export const sharingUnitPlanFragment = {
  unitSections: gql`
    fragment unitSectionsSharingItem on UnitPlan {
      id
      enabledSections {
        userType
        sections
      }
      isSharedWithStudents
      isSharedWithFamilies
      shareWithUserType
    }
  `,
  unitFlow: gql`
    fragment unitFlowSharingItem on UnitPlan {
      id
      unitFlowResources {
        id
      }
    }
  `,
};

export const staffUnitPlanFragment = {
  staffCourses: gql`
    fragment staffCoursesItem on Staff {
      ...userNameFields
      profileImage
      email
      isArchived
      roles
    }
    ${userNameFragment}
  `,
  staffMinorDetails: gql`
    fragment staffCollaboratorItem on Staff {
      id
    }
  `,
};

export const unitPlanFragment = {
  unitPlanDetails: gql`
    fragment unitPlanItem on UnitPlan {
      id
      templateId
      startDate
      endDate
      state
      defaultStaffAccess
      isSharedWithFamilies
      isSharedWithStudents
      updatedBy {
        ...userNameFields
        profileImage
      }
      updatedAt
      createdBy {
        ...userNameFields
        profileImage
      }
      createdAt
      templateId
      template {
        id
        name
      }
      isLibraryPremium
      showLibraryPremiumBannerForUser(filters: $libraryBannerFilters)
      savedBy {
        id
      }
      libraryGrades {
        id
        label
      }
      librarySubjects {
        id
        label
      }
      cascadeUpdatedBy {
        id
        firstName
        middleName
        lastName
        profileImage
        type
      }
      cascadeUpdateAt
      centralRepoConfig(input: $centralRepoInput) {
        newVersionId
        isSeen
        derivedStatus
        publishedDate
      }
      curriculumProgram {
        ...curriculumProgramBasicDetailsItem
      }
      ...unitSectionsSharingItem
      ...unitFlowSharingItem
      grades
      assessmentTool {
        id
      }
      courses {
        ...courseGradeSubjectItem
      }
      unitType {
        ...unitPlanFieldItemWithResponseFilter
      }
      title {
        ...unitPlanFieldItemWithResponseFilter
      }
      unitCode {
        ...unitPlanFieldItemWithResponseFilter
      }
      theme {
        ...unitPlanFieldItemWithResponseFilter
      }
      duration {
        ...unitPlanFieldItemWithResponseFilter
      }
      image {
        ...unitPlanFieldItemWithResponseFilter
      }
      subjects {
        ...unitPlanFieldItemWithResponseFilter
      }
      owner {
        id
        profileImage
        firstName
        middleName
        lastName
      }
      allFields {
        ...unitPlanFieldItemWithResponseFilter
      }
      organization {
        ...organizationItem
      }
      academicYears {
        ...academicYearItem
      }
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        edges {
          permission
          node {
            ...staffCoursesItem
          }
        }
      }
      exceptionalCollaborators(filters: $exceptionalCollaboratorsFilter) {
        totalCount
        edges {
          node {
            id
          }
          permission
        }
      }
      expertProfile {
        ...expertItem
      }
      isDeleted
      isImported
      presetId
      leTemplate
      assessmentTemplate
      submittedToCommunity
      communityComments {
        totalCount
      }
      isNewUnitFlowEnabled
      learningCourse {
        id
        type
        isArchived
        primaryGrade {
          id
          name
        }
        bluePrintCourse {
          id
        }
      }
      classDates {
        classId
        startDate
        endDate
      }
      lockingInfo {
        isLocked
      }
      isUnitFlowLocked
      hasLockedGradingPeriodAssessment
      flowResourceCount {
        totalCount
      }
      isSyncedWithAllTeacherCourseV2 {
        status
        isConflictExists
        courseSyncStatus {
          course {
            id
            title
          }
          status
        }
      }
    }
    ${userNameFragment}
    ${academicYearFragment.academicYear}
    ${curriculumProgramFragment.curriculumProgramBasicDetails}
    ${organizationFragment.organizationBasicDetails}
    ${plannerFieldFragment.unitPlanFieldWithResponseFilter}
    ${staffUnitPlanFragment.staffCourses}
    ${expertProfileFragment.expert}
    ${courseFragment.courseGradeSubjectDetails}
    ${sharingUnitPlanFragment.unitSections}
    ${sharingUnitPlanFragment.unitFlow}
  `,
  unitPlanCollaborator: gql`
    fragment unitPlanItem on UnitPlan {
      id
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        edges {
          permission
          node {
            ...staffCollaboratorItem
          }
        }
      }
    }
    ${staffUnitPlanFragment.staffMinorDetails}
  `,
  unitPlanCollaboratorDetails: gql`
    fragment unitPlanItem on UnitPlan {
      id
      collaborators: allCollaborators(filters: $collaboratorsFilter) {
        totalCount
        edges {
          permission
          node {
            ...staffCoursesItem
          }
        }
      }
    }
    ${staffUnitPlanFragment.staffCourses}
  `,

  unitSummaryPlanDetails: gql`
    fragment unitSummaryPlanItem on UnitPlan {
      id
      grades
      startDate
      endDate
      unitType {
        ...unitPlanFieldItem
      }
      subjects {
        id
        value
      }
      allFields {
        ...unitPlanFieldItem
      }

      template {
        ...localisedTemplateItem
      }

      curriculumProgram {
        id
        type
        nodeInterfaceType
      }
    }
    ${plannerFieldFragment.unitplanField}
    ${plannerTemplateFragment.localisedPlannerTemplate}
  `,
};
